import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAsambleas, getActas, addActa, updateActa, deleteActa, subirArchivoActa, getArchivoUrl } from '../service/ComunidadService';

const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

const FORM_EMPTY = { actaId: 0, asambleaId: null, titulo: '', contenido: '' };

const ComunidadActasAdmin = () => {
    const toast = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const { administracionId, usuarioId } = useAuth();

    const [actas, setActas] = useState([]);
    const [asambleas, setAsambleas] = useState([]);
    const [loading, setLoading] = useState(false);

    const [modalVisible, setModalVisible] = useState(false);
    const [form, setForm] = useState({ ...FORM_EMPTY });
    const [archivo, setArchivo] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const fetchActas = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await getActas(administracionId, barrio.id);
            setActas(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las actas', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, administracionId]);

    useEffect(() => { fetchActas(); }, [fetchActas]);

    useEffect(() => {
        if (!barrio?.id) return;
        getAsambleas(administracionId, barrio.id).then(({ data }) => setAsambleas(data)).catch(() => {});
    }, [barrio?.id, administracionId]);

    const abrirNueva = () => { setForm({ ...FORM_EMPTY }); setArchivo(null); setModalVisible(true); };
    const editar = (row) => { setForm({ actaId: row.actaId, asambleaId: row.asambleaId, titulo: row.titulo, contenido: row.contenido || '' }); setArchivo(null); setModalVisible(true); };

    const guardar = async () => {
        if (!form.asambleaId || !form.titulo) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccioná la asamblea e ingresá el título', life: 3000 });
            return;
        }
        setSubmitting(true);
        try {
            if (form.actaId) {
                await updateActa({ ...form, administracionId });
                if (archivo) await subirArchivoActa(form.actaId, administracionId, archivo);
            } else {
                await addActa({ ...form, barrioId: barrio.id, administracionId, usuarioCreacionId: usuarioId }, archivo);
            }
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Acta guardada correctamente', life: 3000 });
            setModalVisible(false);
            fetchActas();
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el acta', life: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    const eliminar = (row) => {
        confirmDialog({
            message: `¿Eliminar el acta "${row.titulo}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await deleteActa(row.actaId, administracionId);
                    fetchActas();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el acta', life: 3000 });
                }
            },
        });
    };

    return (
        <>
            <h4>Actas</h4>
            <br />
            <ConfirmDialog />
            <Toast ref={toast} />
            <LoadingOverlay visible={loading} />

            <div className="flex align-items-center gap-3 mb-3">
                <Button label="Nueva Acta" icon="pi pi-plus" className="p-button-sm" onClick={abrirNueva} disabled={!asambleas.length} />
            </div>

            <div className="grid">
                <div className="col-12">
                    <DataTable value={actas} paginator rows={15}
                        showGridlines responsiveLayout="scroll"
                        emptyMessage="No hay actas cargadas"
                        className="p-datatable-gridlines bg-white">
                        <Column field="titulo" header="Título" sortable />
                        <Column header="Asamblea" body={(r) => `${fmtFecha(r.asambleaFecha)} — ${r.asambleaTipo}`} style={{ width: '14rem' }} />
                        <Column header="Archivo" style={{ width: '8rem' }} body={(r) => r.archivoUrl
                            ? <a href={getArchivoUrl(r.archivoUrl)} target="_blank" rel="noreferrer"><i className="pi pi-file-pdf" style={{ color: '#d92d20' }} /> Ver</a>
                            : '—'} />
                        <Column header="Acciones" style={{ width: '8rem', textAlign: 'center' }} body={(row) => (
                            <div className="flex gap-2 justify-content-center">
                                <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm" severity="warning" onClick={() => editar(row)} />
                                <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-sm" severity="danger" onClick={() => eliminar(row)} />
                            </div>
                        )} />
                    </DataTable>
                </div>
            </div>

            <Dialog header={form.actaId ? 'Editar Acta' : 'Nueva Acta'} visible={modalVisible} style={{ width: '560px' }} modal
                onHide={() => setModalVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalVisible(false)} disabled={submitting} />
                        <Button label="Guardar" icon="pi pi-check" className="p-button-sm" onClick={guardar} loading={submitting} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    <div className="col-12">
                        <label style={lbl}>Asamblea *</label>
                        <Dropdown value={form.asambleaId} options={asambleas} optionValue="asambleaId"
                            optionLabel="fecha" itemTemplate={(a) => `${fmtFecha(a.fecha)} — ${a.tipo}`} valueTemplate={(a) => a ? `${fmtFecha(a.fecha)} — ${a.tipo}` : 'Seleccionar asamblea'}
                            disabled={!!form.actaId} placeholder="Seleccionar asamblea" onChange={(e) => setForm(f => ({ ...f, asambleaId: e.value }))} className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Título *</label>
                        <InputText value={form.titulo} onChange={(e) => setForm(f => ({ ...f, titulo: e.target.value }))} className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Contenido</label>
                        <InputTextarea value={form.contenido} onChange={(e) => setForm(f => ({ ...f, contenido: e.target.value }))} rows={6} className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>
                            {form.actaId ? 'Reemplazar archivo (PDF, JPG o PNG, opcional)' : 'Archivo (PDF, JPG o PNG, opcional)'}
                        </label>
                        <FileUpload mode="basic" name="file" accept=".pdf,.jpg,.jpeg,.png" maxFileSize={5000000} chooseLabel="Seleccionar archivo"
                            auto={false} customUpload uploadHandler={() => {}}
                            onSelect={(e) => setArchivo(e.files[0])} onClear={() => setArchivo(null)} />
                    </div>
                </div>
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default ComunidadActasAdmin;
